import { getMainPhoto } from '../utils/composite';
// src/pages/CartView.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { shop as shopApi, cart as cartApi } from '../api';
import Icon from '../components/Icon';
import { formatMoney, getCurrencySymbol } from '../data/currencies';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

export default function CartView() {
  const { t } = useTranslation();
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setItems(cartApi.get(shopId));
    shopApi.getById(shopId).then(setShop).catch(() => {}).finally(() => setLoading(false));
  }, [shopId]);

  const updateQty = (productId, qty) => {
    const next = cartApi.setQty(shopId, productId, qty);
    setItems(next);
  };

  const removeItem = (productId) => {
    const next = cartApi.remove(shopId, productId);
    setItems(next);
  };

  const total = items.reduce((s, i) => s + i.qty * i.salePrice, 0);

  const sendToWhatsApp = () => {
    if (!shop?.whatsappNumber) return alert(t('settings.errorWhatsappRequired'));

    const lines = items.map(i =>
      `• ${i.name} × ${i.qty} ${unitLabel(i.unit)} = ${formatMoney(i.qty * i.salePrice, shop?.currency)}`
    );
    const text = [
      t('cart.whatsappGreeting'),
      '',
      ...lines,
      '',
      `${t("cart.total")}: ${formatMoney(total, shop?.currency)}`,
    ].join('\n');

    const url = `https://wa.me/${shop.whatsappNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div className="page" style={{ paddingBottom: items.length ? 160 : 24 }}>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{shop?.name?.toUpperCase()}</p>
          <h1 className="page-title">{t("cart.title")}</h1>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><Icon name="cart" size={28} stroke={1.5} /></div>
          <p>{t("cart.empty")}<br />{t("cart.emptyHint")}</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => {
            const itemKey = item.cartKey || item.id;
            return (
            <div key={itemKey} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12 }}>
              {getMainPhoto(item) ? (
                <img src={getMainPhoto(item)} alt={item.name}
                  style={{ width: 56, height: 56, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: 'var(--r-sm)',
                  background: 'var(--bg-subtle)',
                  color: 'var(--fg-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}><Icon name="package" size={22} /></div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </p>
                <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(item.salePrice, shop?.currency)} / {unitLabel(item.unit)}
                </p>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-subtle)', borderRadius: 'var(--r-xs)', padding: 2 }}>
                    <button onClick={() => updateQty(itemKey, item.qty - 1)} style={qtyBtn}>
                      <Icon name="minus" size={14} stroke={2.5} />
                    </button>
                    <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: 14, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{item.qty}</span>
                    <button onClick={() => updateQty(itemKey, item.qty + 1)} style={qtyBtn}>
                      <Icon name="plus" size={14} stroke={2.5} />
                    </button>
                  </div>
                  <button onClick={() => removeItem(itemKey)}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-display)', fontWeight: 600 }}>
                    {t('common.delete')}
                  </button>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-primary)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  {(item.qty * item.salePrice).toLocaleString()}
                </p>
                <p style={{ fontSize: 10, color: "var(--fg-faint)" }}>{getCurrencySymbol(shop?.currency)}</p>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          padding: 14, paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
          background: 'var(--bg-elevated)',
          borderTop: '1px solid var(--border-subtle)',
          zIndex: 50,
          boxShadow: '0 -4px 20px rgba(15,23,42,0.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 14, color: "var(--fg-muted)" }}>{t("cart.total")}</span>
            <span style={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{formatMoney(total, shop?.currency)}</span>
          </div>
          <button className="btn btn-whatsapp" onClick={sendToWhatsApp}>
            <Icon name="whatsapp" size={18} stroke={0} color="currentColor" />
            {t('cart.sendOrder')}
          </button>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', textAlign: 'center', marginTop: 8 }}>
            {t("cart.whatsappHint")}
          </p>
        </div>
      )}
    </div>
  );
}

const qtyBtn = {
  width: 28, height: 28,
  background: 'transparent', border: 'none',
  color: 'var(--fg)', cursor: 'pointer',
  borderRadius: 'var(--r-xs)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
